import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, ChevronDown, Menu, Bell, LogOut, KeyRound, Eye, EyeOff, Globe,
  Check, X, Timer, Bot, Send, CreditCard, AlertTriangle, Lock, HelpCircle, Medal,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { startFirstSaleOnboarding } from '../utils/firstSaleOnboarding';
import { SETUP_TOUR_EVENT } from '../utils/setupOnboardingTours';
import { useTrial } from '../context/TrialContext';
import { getMenuStructure } from '../utils/menuStructure';
import { CRM_LEGACY_ALIAS_MODULES, CRM_ORG_MODULE_ALIAS_MAP } from '../utils/accessModules';
import { clearStoredSelectedSiteId } from '../utils/sitePreferences';
import { desactivarAvisosAdminEsteDispositivo } from '../utils/pushSubscription';

/**
 * TopbarMenu — Barra superior con menú integrado (sin sidebar lateral).
 * --------------------------------------------------------------
 * - Paleta pastel: mint + crema + grafito.
 * - Diseño cuadrado (border-radius 6-8px).
 * - En escritorio: dropdown por categoría al hover/click.
 * - En mobile/tablet: drawer lateral (botón hamburguesa).
 * - Botón perfil rediseñado con avatar circular sobre tarjeta.
 *
 * Props:
 *  · activeModule, setActiveModule, onNavigate
 *  · permisos (array | Set | null)  → permisos del usuario
 *  · orgModules (array | null)      → módulos habilitados del plan
 *  · userEmail                       → para detectar master admin
 */

const resolveAccessModuleId = (id) => {
  const accessAliasMap = {
    'logistica-entregas': 'logistica',
    'logistica-operativa': 'logistica',
    'produccion-flujos': 'pedidos-produccion',
  };
  return accessAliasMap[id] || id;
};

export default function TopbarMenu({
  activeModule,
  setActiveModule,
  onNavigate,
  permisos = null,
  orgModules = null,
  userEmail = '',
  setupProgressPct = 0,
  showSetupProgress = false,
  setupProgressDone = false,
}) {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const { sites: sitios, selectedSite: sitioSeleccionado, setSelectedSiteId, loading: loadingSites } = useSite();
  const {
    daysLeft,
    isTrialing,
    isBlocked,
    isPending,
    isGrace,
    isExpired,
    isReadOnly,
    graceDaysLeft,
    showWarning,
  } = useTrial();

  const isMasterAdmin = userEmail?.toLowerCase() === 'adpenia@gmail.com';

  const handleRestartTutorial = () => {
    if (!profile?.organization_id) {
      toast.error('No se pudo encontrar la organización activa.');
      return;
    }
    startFirstSaleOnboarding({
      organizationId: profile.organization_id,
      userId: profile?.id || user?.id,
    });
    // El reinicio descarta la guía que estuviera abierta: avisamos para que el
    // panel de esa guía desaparezca sin necesidad de recargar la pantalla.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SETUP_TOUR_EVENT));
    }
    navigate('/sistema/dashboard', { replace: true });
    toast.success('¡Tutorial interactivo reiniciado! Sigue los pasos en pantalla.');
  };

  // ── State ──
  const [openDropdown, setOpenDropdown] = useState(null); // id de la sección abierta
  const [mobileOpen, setMobileOpen] = useState(false);
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pwPanelOpen, setPwPanelOpen] = useState(false);
  const [pwShow, setPwShow] = useState(false);
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { id: 1, text: '¡Hola! Soy el asistente de Gastify. ¿En qué te ayudo?', sender: 'bot' },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatTyping, setChatTyping] = useState(false);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1024px)').matches;
  });

  const dropdownRef = useRef(null);
  const siteRef = useRef(null);
  const userRef = useRef(null);
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);

  // ── Responsive listener ──
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 1024px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener?.('change', handler) || mq.addListener?.(handler);
    return () => {
      mq.removeEventListener?.('change', handler) || mq.removeListener?.(handler);
    };
  }, []);

  // ── Click outside ──
  useEffect(() => {
    const onClick = (e) => {
      const t = e.target;
      if (openDropdown && dropdownRef.current && !dropdownRef.current.contains(t)) setOpenDropdown(null);
      if (siteDropdownOpen && siteRef.current && !siteRef.current.contains(t)) setSiteDropdownOpen(false);
      if (userMenuOpen && userRef.current && !userRef.current.contains(t)) {
        setUserMenuOpen(false);
        setPwPanelOpen(false);
      }
      if (chatOpen && chatRef.current && !chatRef.current.contains(t)) setChatOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setOpenDropdown(null);
        setSiteDropdownOpen(false);
        setUserMenuOpen(false);
        setPwPanelOpen(false);
        setMobileOpen(false);
        setChatOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [openDropdown, siteDropdownOpen, userMenuOpen, chatOpen]);

  // ── Permisos ──
  const permisosSet = useMemo(() => {
    if (permisos == null) return null;
    let raw = permisos;
    if (typeof raw === 'object' && !Array.isArray(raw) && !(raw instanceof Set)) {
      raw = raw.effective;
      if (raw == null) return null;
    }
    let asSet;
    if (raw instanceof Set) asSet = raw;
    else if (Array.isArray(raw)) asSet = new Set(raw);
    else return null;
    if (asSet.size === 0) return null;
    if (asSet.has('all')) return null;
    return asSet;
  }, [permisos]);

  const orgModulesSet = useMemo(
    () => (orgModules === null ? null : new Set(orgModules)),
    [orgModules]
  );

  const hasOrgModuleAccess = (id) => {
    const resolved = resolveAccessModuleId(id);
    if (!resolved || resolved.startsWith('super-admin-')) return true;
    if (orgModulesSet === null) return true;
    const allowed = [resolved, ...(CRM_ORG_MODULE_ALIAS_MAP[resolved] || [])];
    return allowed.some((m) => orgModulesSet.has(m));
  };

  const hasPermission = (id) => {
    const resolved = resolveAccessModuleId(id);
    if (resolved?.startsWith('super-admin-')) return isMasterAdmin;
    if (!hasOrgModuleAccess(resolved)) return false;
    if (isMasterAdmin) return true;
    if (permisosSet === null) return true;
    const keys = [id, resolved, ...(CRM_LEGACY_ALIAS_MODULES[id] || [])];
    return keys.some((k) => permisosSet.has(k));
  };

  // ── Navegación ──
  const handleNavigate = (id) => {
    const aliasMap = {
      configventas: 'config-ventas-cobros',
      configfinanzas: 'config-finanzas-impuestos',
      configSistema: 'config-sistema-datos',
    };
    const resolved = aliasMap[id] || id;
    setOpenDropdown(null);
    setMobileOpen(false);
    if (typeof onNavigate === 'function') onNavigate(resolved);
    if (typeof setActiveModule === 'function') setActiveModule(resolved);
  };

  // ── Estructura ──
  const sections = useMemo(() => {
    return getMenuStructure(isMasterAdmin)
      .map((sec) => ({
        ...sec,
        items: sec.items.filter((it) => hasPermission(it.id)),
      }))
      .filter((sec) => sec.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMasterAdmin, permisosSet, orgModulesSet]);

  const isSectionActive = (sec) =>
    sec.items.some((it) => activeModule === (it.routeId || it.id));

  // ── Trial / Pago badge ──
  // Siempre que toque pagar, mostramos un chip claro y clickeable.
  //   tone='info'      → trial con holgura: invita a activar
  //   tone='warn'      → trial por vencer / gracia: urgencia leve
  //   tone='danger'    → vencido / read-only / bloqueado: hay que pagar ya
  // target: ruta a la que lleva el click.
  // icon: componente para reforzar el mensaje visual.
  const trialBadge = () => {
    if (isBlocked) {
      return {
        label: 'Cuenta bloqueada · contactar soporte',
        short: 'Bloqueada',
        tone: 'danger',
        icon: Lock,
        target: '/sistema/pago-requerido',
      };
    }
    if (isExpired || isReadOnly) {
      return {
        label: 'Pagar mi plan',
        short: 'Pagar',
        tone: 'danger',
        icon: CreditCard,
        target: '/sistema/gestion-plan',
      };
    }
    if (isGrace) {
      const dias = graceDaysLeft > 0
        ? ` (${graceDaysLeft} día${graceDaysLeft !== 1 ? 's' : ''})`
        : '';
      return {
        label: `Pagar mi plan${dias}`,
        short: 'Pagar',
        tone: 'warn',
        icon: AlertTriangle,
        target: '/sistema/gestion-plan',
      };
    }
    if (isPending) {
      return {
        label: 'Pago en proceso',
        short: 'Pago en proceso',
        tone: 'warn',
        icon: Timer,
        target: '/sistema/gestion-plan',
      };
    }
    if (isTrialing) {
      const dias = `${daysLeft} día${daysLeft !== 1 ? 's' : ''}`;
      if (showWarning) {
        return {
          label: `Pagar — quedan ${dias}`,
          short: 'Pagar',
          tone: 'warn',
          icon: CreditCard,
          target: '/sistema/gestion-plan',
        };
      }
      return {
        label: `${dias} gratis · pagar plan`,
        short: `Pagar (${dias})`,
        tone: 'info',
        icon: CreditCard,
        target: '/sistema/gestion-plan',
      };
    }
    return null;
  };
  const badge = trialBadge();

  // Estilos por tono — más grande que el chip mini, lo suficiente para leerse y clickearse.
  const badgeToneStyles = {
    info: {
      bg: 'var(--gst-mint-100)',
      border: 'var(--gst-mint-300)',
      text: 'var(--gst-mint-700)',
      hover: 'var(--gst-mint-200)',
    },
    warn: {
      bg: '#FEF3C7',
      border: '#F59E0B',
      text: '#92400E',
      hover: '#FDE68A',
    },
    danger: {
      bg: '#FEE2E2',
      border: '#EF4444',
      text: '#991B1B',
      hover: '#FECACA',
    },
  };

  // ── Sucursal ──
  const handleSiteSelect = (s) => {
    if (!s?.id) return;
    setSelectedSiteId(s.id);
    setSiteDropdownOpen(false);
    toast.success(`Sucursal: ${s.nombre}`);
  };

  // ── Logout ──
  const handleLogout = async () => {
    try {
      // Avisos push: este equipo deja de recibir los pedidos de la empresa.
      await desactivarAvisosAdminEsteDispositivo();
      await supabase.auth.signOut();
      clearStoredSelectedSiteId();
      navigate('/login');
    } catch (e) {
      toast.error('No se pudo cerrar sesión');
    }
  };

  // ── Cambio de contraseña ──
  const handleChangePassword = async () => {
    if (!pwNew || pwNew.length < 8) return toast.error('La contraseña debe tener al menos 8 caracteres');
    if (pwNew !== pwConfirm) return toast.error('La confirmación no coincide');
    try {
      setPwLoading(true);
      const { error } = await supabase.auth.updateUser({ password: pwNew });
      if (error) throw error;
      toast.success('Contraseña actualizada');
      setPwNew('');
      setPwConfirm('');
      setPwPanelOpen(false);
      setUserMenuOpen(false);
    } catch (e) {
      toast.error(e?.message || 'Error');
    } finally {
      setPwLoading(false);
    }
  };

  // ── Chatbot ──
  useEffect(() => {
    if (chatOpen) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [chatOpen, chatMessages]);

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatTyping) return;
    const text = chatInput.trim();
    setChatMessages((p) => [...p, { id: Date.now(), text, sender: 'user' }]);
    setChatInput('');
    setChatTyping(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const { data, error } = await supabase.functions.invoke('chat-gastify', {
        body: { message: text },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });
      if (error) throw error;
      const reply = data?.reply || data?.message || 'No pude obtener respuesta.';
      setChatMessages((p) => [...p, { id: Date.now() + 1, text: reply, sender: 'bot' }]);
    } catch (err) {
      setChatMessages((p) => [...p, { id: Date.now() + 1, text: 'Error de conexión.', sender: 'bot' }]);
    } finally {
      setChatTyping(false);
    }
  };

  // ──────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────
  const initials = (profile?.full_name || user?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');

  return (
    <>
      <header
        className="sticky top-0 z-40 w-full overflow-visible border-b"
        style={{
          background: 'var(--gst-cream-50)',
          borderColor: 'var(--gst-border)',
        }}
      >
        {/* ── FILA SUPERIOR: brand + sucursal + perfil ── */}
        <div className="flex items-center justify-between px-3 sm:px-5 h-14"
             style={{ borderBottom: '1px solid var(--gst-border)' }}>

          {/* LEFT: brand + hamburger en mobile */}
          <div className="flex items-center gap-3">
            {isMobile && (
              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="p-2 rounded-md hover:bg-[color:var(--gst-cream-100)]"
                aria-label="Abrir menú"
              >
                <Menu size={20} style={{ color: 'var(--gst-graphite-700)' }} />
              </button>
            )}

            <button
              onClick={() => navigate('/sistema')}
              className="flex items-center gap-2 group"
              title="Inicio"
            >
              <div
                className="h-9 w-9 rounded-md flex items-center justify-center font-black text-white text-[16px] shadow-sm"
                style={{ background: 'var(--gst-mint-500)' }}
              >
                G
              </div>
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-[15px] font-extrabold tracking-tight" style={{ color: 'var(--gst-graphite-900)', fontFamily: 'Manrope, sans-serif' }}>
                  Gastify
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[0.18em] mt-0.5" style={{ color: 'var(--gst-mint-600)' }}>
                  Gestioná sin complicarte
                </span>
              </div>
            </button>

            {badge && (() => {
              const tone = badgeToneStyles[badge.tone] || badgeToneStyles.info;
              const BadgeIcon = badge.icon || CreditCard;
              const tooltip = isTrialing
                ? 'Pagar tu plan ahora — los días gratis se suman al primer mes.'
                : isGrace
                ? 'Tu prueba terminó. Pagá para seguir operando sin restricciones.'
                : isExpired || isReadOnly
                ? 'Tu suscripción venció. Pagá para reactivar la cuenta.'
                : isBlocked
                ? 'Tu cuenta está bloqueada. Contactá al soporte.'
                : 'Pagar plan';
              return (
                <button
                  type="button"
                  onClick={() => navigate(badge.target)}
                  title={tooltip}
                  aria-label={tooltip}
                  className="group inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-black uppercase tracking-[0.08em] border transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
                  style={{
                    background: tone.bg,
                    borderColor: tone.border,
                    color: tone.text,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = tone.hover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = tone.bg; }}
                >
                  <BadgeIcon size={13} className={badge.tone === 'danger' ? 'animate-pulse' : ''} />
                  <span className="hidden sm:inline">{badge.label}</span>
                  <span className="sm:hidden">{badge.short || badge.label}</span>
                </button>
              );
            })()}
          </div>

          {/* RIGHT: sucursal + academia + chat + notif + perfil */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {showSetupProgress && (
              // Al 100% el boton no se va: cambia de tono y sigue siendo la
              // puerta de entrada a las tareas opcionales y a repetir las guias.
              <button
                type="button"
                data-onboarding-id="topbar-menu-item-progreso"
                onClick={() => onNavigate('progreso')}
                title={setupProgressDone
                  ? 'Puesta a punto completa — entrá para ver tareas opcionales y repetir las guías'
                  : 'Seguí tu puesta a punto'}
                aria-label={setupProgressDone
                  ? 'Puesta a punto completa. Ver tareas opcionales y guías'
                  : `Empezá a operar, ${setupProgressPct}% completado`}
                className={`flex items-center gap-1.5 px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-[10.5px] font-black uppercase tracking-wider text-white shadow-lg transition-all hover:scale-105 active:scale-95 shrink-0 ${
                  setupProgressDone
                    ? 'shadow-emerald-500/25 hover:shadow-emerald-500/40'
                    : 'shadow-purple-500/25 hover:shadow-purple-500/40'
                }`}
                style={{
                  background: setupProgressDone
                    ? 'linear-gradient(135deg, #059669 0%, #0d9488 100%)'
                    : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                }}
              >
                {setupProgressDone ? (
                  <Medal size={13} className="shrink-0" />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
                )}
                <span className="hidden sm:inline">
                  {setupProgressDone ? 'Puesta a punto 100%' : `Empezá a operar ${setupProgressPct}%`}
                </span>
                <span className="sm:hidden">{setupProgressDone ? '100%' : `${setupProgressPct}%`}</span>
              </button>
            )}
            {/* Sucursal */}
            <div className="relative" ref={siteRef}>
              <button
                data-onboarding-id="topbar-site-selector"
                onClick={() => setSiteDropdownOpen((v) => !v)}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md border transition-all"
                style={{
                  background: 'var(--gst-surface)',
                  borderColor: 'var(--gst-border)',
                  color: 'var(--gst-graphite-700)',
                }}
              >
                <Globe size={14} style={{ color: 'var(--gst-mint-600)' }} />
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider max-w-[70px] sm:max-w-[120px] truncate">
                  {loadingSites ? '...' : (sitioSeleccionado?.nombre || 'Sin sitio')}
                </span>
                <ChevronDown size={12} className={`transition-transform ${siteDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {siteDropdownOpen && (
                <div
                  className="absolute right-0 top-full mt-1.5 w-72 rounded-md border shadow-lg overflow-hidden z-50"
                  style={{ background: 'var(--gst-surface)', borderColor: 'var(--gst-border)' }}
                >
                  <div className="px-3 py-2 text-[10px] font-black uppercase tracking-widest border-b"
                       style={{ background: 'var(--gst-cream-100)', borderColor: 'var(--gst-border)', color: 'var(--gst-graphite-500)' }}>
                    Seleccionar sucursal
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {sitios.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => handleSiteSelect(s)}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[color:var(--gst-cream-50)] text-left"
                      >
                        <div>
                          <div className="text-[12px] font-bold" style={{ color: 'var(--gst-graphite-900)' }}>{s.nombre}</div>
                          <div className="text-[10px]" style={{ color: 'var(--gst-graphite-500)' }}>{s.direccion || 'Sin dirección'}</div>
                        </div>
                        {sitioSeleccionado?.id === s.id && <Check size={14} style={{ color: 'var(--gst-mint-600)' }} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Ayuda / Tutorial interactivo */}
            <button
              type="button"
              onClick={handleRestartTutorial}
              className="hidden md:flex h-10 w-10 rounded-md items-center justify-center border transition-all hover:bg-[color:var(--gst-cream-50)]"
              style={{
                background: 'var(--gst-surface)',
                borderColor: 'var(--gst-border)',
                color: 'var(--gst-graphite-700)',
              }}
              title="Reactivar Tutorial Interactivo"
              aria-label="Reactivar Tutorial Interactivo"
            >
              <HelpCircle size={18} />
            </button>

            {/* Academia */}
            <button
              type="button"
              data-onboarding-id="topbar-academia"
              onClick={() => handleNavigate('academia')}
              className="hidden md:flex h-10 w-10 rounded-md items-center justify-center border transition-all"
              style={{
                background: activeModule === 'academia' ? 'var(--gst-mint-500)' : 'var(--gst-surface)',
                borderColor: activeModule === 'academia' ? 'var(--gst-mint-500)' : 'var(--gst-border)',
                color: activeModule === 'academia' ? '#fff' : 'var(--gst-graphite-700)',
              }}
              title="Academia"
              aria-label="Academia"
            >
              <BookOpen size={18} />
            </button>

            {/* Chatbot */}
            <div className="relative" ref={chatRef}>
              <button
                onClick={() => setChatOpen((v) => !v)}
                className={`h-9 w-9 sm:h-10 sm:w-10 rounded-md flex items-center justify-center border transition-all ${
                  chatOpen ? 'text-white' : ''
                }`}
                style={{
                  background: chatOpen ? 'var(--gst-mint-500)' : 'var(--gst-surface)',
                  borderColor: chatOpen ? 'var(--gst-mint-500)' : 'var(--gst-border)',
                  color: chatOpen ? '#fff' : 'var(--gst-graphite-700)',
                }}
                title="Asistente IA"
              >
                <Bot size={18} />
              </button>

              {chatOpen && (
                <div
                  className="fixed inset-x-3 top-16 max-h-[80vh] sm:inset-auto sm:absolute sm:right-0 sm:top-full sm:mt-1.5 sm:w-[360px] sm:h-[460px] sm:max-w-[90vw] rounded-md border shadow-xl flex flex-col overflow-hidden z-50"
                  style={{ background: 'var(--gst-surface)', borderColor: 'var(--gst-border)' }}
                >
                  <div className="flex items-center justify-between px-3 py-2 border-b"
                       style={{ background: 'var(--gst-cream-100)', borderColor: 'var(--gst-border)' }}>
                    <div className="flex items-center gap-2">
                      <Bot size={14} style={{ color: 'var(--gst-mint-600)' }} />
                      <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: 'var(--gst-graphite-900)' }}>Asistente IA</span>
                    </div>
                    <button onClick={() => setChatOpen(false)}><X size={14} style={{ color: 'var(--gst-graphite-500)' }} /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ background: 'var(--gst-cream-50)' }}>
                    {chatMessages.map((m) => (
                      <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[85%] p-2.5 rounded-md text-[12px] leading-relaxed`}
                          style={{
                            background: m.sender === 'user' ? 'var(--gst-mint-500)' : 'var(--gst-surface)',
                            color: m.sender === 'user' ? '#fff' : 'var(--gst-graphite-900)',
                            border: m.sender === 'user' ? 'none' : '1px solid var(--gst-border)',
                          }}
                        >
                          {m.text}
                        </div>
                      </div>
                    ))}
                    {chatTyping && (
                      <div className="flex justify-start">
                        <div className="px-3 py-2 rounded-md" style={{ background: 'var(--gst-surface)', border: '1px solid var(--gst-border)' }}>
                          <span className="inline-flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--gst-graphite-300)' }}></span>
                            <span className="w-1.5 h-1.5 rounded-full animate-bounce delay-100" style={{ background: 'var(--gst-graphite-300)' }}></span>
                            <span className="w-1.5 h-1.5 rounded-full animate-bounce delay-200" style={{ background: 'var(--gst-graphite-300)' }}></span>
                          </span>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  <form onSubmit={handleChatSubmit} className="p-2 border-t flex gap-2"
                        style={{ borderColor: 'var(--gst-border)' }}>
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Escribí tu consulta..."
                      className="flex-1 px-3 py-2 rounded-md text-[12px] outline-none"
                      style={{ background: 'var(--gst-cream-50)', border: '1px solid var(--gst-border)', color: 'var(--gst-graphite-900)' }}
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || chatTyping}
                      className="px-3 rounded-md text-white disabled:opacity-40"
                      style={{ background: 'var(--gst-mint-500)' }}
                    >
                      <Send size={14} />
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Notificaciones (placeholder, mantiene comportamiento) */}
            <button
              onClick={() => navigate('/sistema/notificaciones')}
              className="hidden sm:flex h-10 w-10 rounded-md items-center justify-center border"
              style={{ background: 'var(--gst-surface)', borderColor: 'var(--gst-border)', color: 'var(--gst-graphite-700)' }}
              title="Notificaciones"
            >
              <Bell size={18} />
            </button>

            {/* === BOTÓN PERFIL REDISEÑADO === */}
            <div className="relative" ref={userRef}>
              <button
                data-onboarding-id="topbar-user-menu"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-md border transition-all hover:shadow-sm"
                style={{ background: 'var(--gst-surface)', borderColor: 'var(--gst-border)' }}
              >
                <div
                  className="h-8 w-8 rounded-md flex items-center justify-center font-black text-white text-[12px] tracking-wider"
                  style={{
                    background: 'linear-gradient(135deg, var(--gst-mint-400), var(--gst-mint-600))',
                  }}
                >
                  {initials || '?'}
                </div>
                <div className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-[12px] font-extrabold max-w-[120px] truncate" style={{ color: 'var(--gst-graphite-900)' }}>
                    {profile?.full_name || (user?.email ? user.email.split('@')[0] : 'Usuario')}
                  </span>
                  <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider" style={{ color: 'var(--gst-mint-600)' }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--gst-mint-500)' }}></span>
                    En línea
                  </span>
                </div>
                <ChevronDown size={12} style={{ color: 'var(--gst-graphite-500)' }} />
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1.5 w-[300px] rounded-md border shadow-xl overflow-hidden z-50"
                  style={{ background: 'var(--gst-surface)', borderColor: 'var(--gst-border)' }}
                >
                  <div className="p-4 border-b flex items-center gap-3"
                       style={{ background: 'var(--gst-cream-100)', borderColor: 'var(--gst-border)' }}>
                    <div
                      className="h-10 w-10 rounded-md flex items-center justify-center font-black text-white text-[14px]"
                      style={{ background: 'linear-gradient(135deg, var(--gst-mint-400), var(--gst-mint-600))' }}
                    >
                      {initials || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-extrabold truncate" style={{ color: 'var(--gst-graphite-900)' }}>
                        {profile?.full_name || 'Mi cuenta'}
                      </div>
                      <div className="text-[11px] truncate" style={{ color: 'var(--gst-graphite-500)' }}>
                        {user?.email || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    <button
                      onClick={() => setPwPanelOpen((v) => !v)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-[color:var(--gst-cream-50)] transition-all"
                    >
                      <span className="flex items-center gap-2">
                        <KeyRound size={14} style={{ color: 'var(--gst-graphite-500)' }} />
                        <span className="text-[12px] font-bold" style={{ color: 'var(--gst-graphite-900)' }}>
                          Cambiar contraseña
                        </span>
                      </span>
                      <ChevronDown size={12} className={`transition-transform ${pwPanelOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--gst-graphite-500)' }} />
                    </button>

                    {pwPanelOpen && (
                      <div className="p-3 my-1 rounded-md space-y-2"
                           style={{ background: 'var(--gst-cream-50)', border: '1px solid var(--gst-border)' }}>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--gst-graphite-500)' }}>Seguridad</span>
                          <button onClick={() => setPwShow((v) => !v)}>
                            {pwShow ? <EyeOff size={12} style={{ color: 'var(--gst-graphite-500)' }} /> : <Eye size={12} style={{ color: 'var(--gst-graphite-500)' }} />}
                          </button>
                        </div>
                        <input
                          type={pwShow ? 'text' : 'password'}
                          value={pwNew}
                          onChange={(e) => setPwNew(e.target.value)}
                          placeholder="Nueva contraseña (mín. 8)"
                          className="w-full px-3 py-2 rounded-md text-[12px] outline-none"
                          style={{ background: 'var(--gst-surface)', border: '1px solid var(--gst-border)', color: 'var(--gst-graphite-900)' }}
                        />
                        <input
                          type={pwShow ? 'text' : 'password'}
                          value={pwConfirm}
                          onChange={(e) => setPwConfirm(e.target.value)}
                          placeholder="Confirmar contraseña"
                          className="w-full px-3 py-2 rounded-md text-[12px] outline-none"
                          style={{ background: 'var(--gst-surface)', border: '1px solid var(--gst-border)', color: 'var(--gst-graphite-900)' }}
                        />
                        <button
                          onClick={handleChangePassword}
                          disabled={pwLoading}
                          className="w-full py-2 rounded-md text-[11px] font-black uppercase tracking-widest text-[var(--color-accent-dark)] disabled:opacity-50"
                          style={{ background: 'var(--gst-mint-500)' }}
                        >
                          {pwLoading ? 'Actualizando...' : 'Guardar'}
                        </button>
                      </div>
                    )}

                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md mt-1 transition-all hover:opacity-90"
                      style={{ background: 'var(--gst-danger-bg)', color: 'var(--gst-danger-text)' }}
                    >
                      <LogOut size={14} />
                      <span className="text-[11px] font-black uppercase tracking-widest">Cerrar sesión</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isMobile && (
          <nav
            data-onboarding-id="topbar-menu-items-group"
            className="h-12 overflow-visible px-3 sm:px-5"
            ref={dropdownRef}
            style={{ background: 'var(--gst-cream-100)' }}
          >
            <div className="flex h-full items-center gap-1 overflow-visible whitespace-nowrap">
              {sections.map((sec) => {
              const Icon = sec.icon;
              const isOpen = openDropdown === sec.id;
              const isActive = isSectionActive(sec);
              const hasOnlyDirect = sec.items.length === 1 && sec.items[0].isDirect;

              return (
                <div key={sec.id} className="relative">
                  <button
                    data-onboarding-id={`topbar-menu-item-${sec.id}`}
                    onClick={() => {
                      if (hasOnlyDirect) {
                        handleNavigate(sec.items[0].id);
                      } else {
                        setOpenDropdown(isOpen ? null : sec.id);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-bold whitespace-nowrap transition-all ${
                      isActive || isOpen
                        ? 'shadow-sm'
                        : 'hover:bg-[color:var(--gst-cream-50)]'
                    }`}
                    style={{
                      background: isActive || isOpen ? 'var(--gst-mint-500)' : 'transparent',
                      color: isActive || isOpen ? '#fff' : 'var(--gst-graphite-900)',
                    }}
                  >
                    <Icon size={14} />
                    {sec.label}
                    {!hasOnlyDirect && <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
                  </button>

                  {isOpen && !hasOnlyDirect && (
                    <div
                      className="absolute left-0 top-full mt-1 min-w-[280px] rounded-md border shadow-xl overflow-hidden z-50"
                      style={{ background: 'var(--gst-surface)', borderColor: 'var(--gst-border)' }}
                    >
                      <div className="py-1">
                        {sec.items.map((it) => {
                          const ItemIcon = it.icon;
                          const targetId = it.routeId || it.id;
                          const itemActive = activeModule === targetId;
                          return (
                            <button
                              key={`${sec.id}-${it.id}`}
                              data-onboarding-id={`topbar-menu-subitem-${it.id}`}
                              onClick={() => handleNavigate(targetId)}
                              className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left transition-all`}
                              style={{
                                background: itemActive ? 'var(--gst-mint-100)' : 'transparent',
                                color: itemActive ? 'var(--gst-mint-700)' : 'var(--gst-graphite-900)',
                              }}
                              onMouseEnter={(e) => {
                                if (!itemActive) e.currentTarget.style.background = 'var(--gst-cream-50)';
                              }}
                              onMouseLeave={(e) => {
                                if (!itemActive) e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span className="flex items-center gap-2.5">
                                <ItemIcon size={14} style={{ color: itemActive ? 'var(--gst-mint-600)' : 'var(--gst-graphite-500)' }} />
                                <span className="text-[12px] font-semibold">{it.label}</span>
                              </span>
                              {it.badge && (
                                <span className="text-[9px] font-black px-1.5 py-0.5 rounded"
                                      style={{ background: 'var(--gst-mint-200)', color: 'var(--gst-mint-700)' }}>
                                  {it.badge}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </nav>
        )}
      </header>

      {/* ── DRAWER MOBILE ── */}
      {isMobile && mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="fixed top-0 left-0 bottom-0 z-50 w-[300px] max-w-[85vw] overflow-y-auto"
            style={{ background: 'var(--gst-surface)', borderRight: '1px solid var(--gst-border)' }}
          >
            <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: 'var(--gst-border)' }}>
              <div className="flex items-center gap-2">
                <div
                  className="h-9 w-9 rounded-md flex items-center justify-center font-black text-white"
                  style={{ background: 'var(--gst-mint-500)' }}
                >G</div>
                <div className="flex flex-col">
                  <span className="text-[14px] font-extrabold" style={{ color: 'var(--gst-graphite-900)' }}>Gastify</span>
                  <span className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--gst-mint-600)' }}>Gestioná sin complicarte</span>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)}><X size={18} style={{ color: 'var(--gst-graphite-500)' }} /></button>
            </div>

            <nav className="p-2">
              {sections.map((sec) => {
                const Icon = sec.icon;
                const isOpen = openDropdown === sec.id;
                const hasOnlyDirect = sec.items.length === 1 && sec.items[0].isDirect;
                return (
                  <div key={sec.id} className="mb-1">
                    <button
                      data-onboarding-id={`topbar-menu-item-${sec.id}-m`}
                      onClick={() => {
                        if (hasOnlyDirect) handleNavigate(sec.items[0].id);
                        else setOpenDropdown(isOpen ? null : sec.id);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-[color:var(--gst-cream-50)]"
                    >
                      <span className="flex items-center gap-2">
                        <Icon size={16} style={{ color: 'var(--gst-mint-600)' }} />
                        <span className="text-[13px] font-bold" style={{ color: 'var(--gst-graphite-900)' }}>{sec.label}</span>
                      </span>
                      {!hasOnlyDirect && <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--gst-graphite-500)' }} />}
                    </button>

                    {isOpen && !hasOnlyDirect && (
                      <div className="ml-7 mt-1 space-y-0.5">
                        {sec.items.map((it) => {
                          const ItemIcon = it.icon;
                          const targetId = it.routeId || it.id;
                          const itemActive = activeModule === targetId;
                          return (
                            <button
                              key={`${sec.id}-${it.id}-m`}
                              data-onboarding-id={`topbar-menu-subitem-${it.id}-m`}
                              onClick={() => handleNavigate(targetId)}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left"
                              style={{
                                background: itemActive ? 'var(--gst-mint-100)' : 'transparent',
                                color: itemActive ? 'var(--gst-mint-700)' : 'var(--gst-graphite-700)',
                              }}
                            >
                              <ItemIcon size={12} />
                              <span className="text-[12px] font-semibold">{it.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="mt-4 pt-3 border-t space-y-1" style={{ borderColor: 'var(--gst-border)' }}>
                <button
                  onClick={() => {
                    handleNavigate('academia');
                    setMobileOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md hover:bg-[color:var(--gst-cream-50)] text-left"
                >
                  <BookOpen size={16} style={{ color: 'var(--gst-mint-600)' }} />
                  <span className="text-[13px] font-bold" style={{ color: 'var(--gst-graphite-900)' }}>Academia</span>
                </button>
                <button
                  onClick={() => {
                    handleRestartTutorial();
                    setMobileOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md hover:bg-[color:var(--gst-cream-50)] text-left"
                >
                  <HelpCircle size={16} style={{ color: 'var(--gst-mint-600)' }} />
                  <span className="text-[13px] font-bold" style={{ color: 'var(--gst-graphite-900)' }}>Reactivar Tutorial</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/sistema/notificaciones');
                    setMobileOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md hover:bg-[color:var(--gst-cream-50)] text-left"
                >
                  <Bell size={16} style={{ color: 'var(--gst-mint-600)' }} />
                  <span className="text-[13px] font-bold" style={{ color: 'var(--gst-graphite-900)' }}>Notificaciones</span>
                </button>
              </div>
            </nav>
          </aside>
        </>
      )}
    </>
  );
}
